import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { BarChart3, Store, Trophy, Warehouse, AlertTriangle, Gauge, Package, Inbox } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import {
  AggHeader, ResetSampleButton, TabBar, PeriodSelect, Kpi, Panel, useSort, SortTh, TH_ROW, TD, inr, inrCompact, num, pct,
  TableFilter, textMatch, CsvButton, downloadCsv, ScoreBar, Pill, CHART_COLORS,
} from "./aggKit";

const TABS = [["msme", "MSME performance matrix", Store], ["carriers", "3PL carrier SLA leaderboard", Trophy], ["hubs", "Franchise hub utilisation", Warehouse]];
const TT = { background: "#0B1220", border: "1px solid #26324d", fontSize: 11, color: "#fff" };

function useData(path, params, tick) {
  const [d, setD] = useState(null);
  const key = JSON.stringify(params);
  const load = useCallback(() => { api.get(path, { params: JSON.parse(key) }).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, [path, key]);
  useEffect(() => { load(); }, [load, tick]);
  return d;
}

function MsmeMatrix({ tick }) {
  const [days, setDays] = useState(90);
  const [q, setQ] = useState("");
  const d = useData("/agg/analytics/msme", { days }, tick);
  const rows = useMemo(() => (d?.items || []).filter((x) => textMatch(x, q, ["name", "category", "city"])), [d, q]);
  const sort = useSort(rows, "rank", "asc");
  const exp = () => downloadCsv(`msme-performance-${days || "all"}d.csv`, sort.sorted, [
    ["rank", "Rank"], ["name", "Merchant"], ["category", "Category"], ["city", "City"], ["shipments", "Shipments"], ["gmv", "GMV (INR)"],
    ["avg_ticket", "Avg ticket (INR)"], ["rto_pct", "RTO %"], ["cod_share_pct", "COD share %"], ["payment_conversion_pct", "Payment conversion %"],
    ["revenue", "Revenue (INR)"], ["profit", "Profit (INR)"], ["margin_pct", "Margin %"], ["score", "Score"]]);
  return (
    <Panel testid="msme-matrix" title="MSME performance matrix"
      subtitle={d ? `Ranked by score: volume ${d.weights.volume}% · ticket size ${d.weights.avg_ticket}% · low RTO ${d.weights.low_rto}% · payment conversion ${d.weights.payment_conversion}% (COD orders that turned into collected cash)` : ""}
      actions={<><div className="[&_select]:bg-background [&_select]:text-foreground [&_select]:border-input"><PeriodSelect value={days} onChange={setDays} testid="msme-period" /></div><TableFilter testid="msme-search" value={q} onChange={setQ} placeholder="Filter merchant, category…" /><CsvButton onClick={exp} testid="msme-csv" /></>}
      bodyClass="overflow-x-auto">
      {!d ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : (
        <table className="w-full text-xs">
          <thead className={TH_ROW}><tr>
            <SortTh k="rank" label="#" sort={sort} /><SortTh k="name" label="Merchant" sort={sort} /><SortTh k="shipments" label="Shipments" sort={sort} align="right" />
            <SortTh k="gmv" label="GMV" sort={sort} align="right" /><SortTh k="avg_ticket" label="Avg ticket" sort={sort} align="right" />
            <SortTh k="rto_pct" label="RTO %" sort={sort} align="right" /><SortTh k="cod_share_pct" label="COD share" sort={sort} align="right" />
            <SortTh k="payment_conversion_pct" label="Payment conv." sort={sort} align="right" /><SortTh k="revenue" label="Revenue" sort={sort} align="right" />
            <SortTh k="margin_pct" label="Margin" sort={sort} align="right" /><SortTh k="score" label="Score" sort={sort} />
          </tr></thead>
          <tbody className="divide-y divide-border">
            {sort.sorted.map((x) => (
              <tr key={x.merchant_id} className="hover:bg-muted/40" data-testid={`msme-row-${x.rank}`}>
                <td className={`${TD} font-heading font-black text-base ${x.rank <= 3 ? "text-primary" : "text-muted-foreground"}`}>{x.rank}</td>
                <td className={TD}><Link to={`/dashboard/agg/shipments?merchant_id=${x.merchant_id}`} className="font-semibold hover:text-primary">{x.name}</Link><div className="text-[10px] text-muted-foreground">{x.category} · {x.city}</div></td>
                <td className={`${TD} text-right font-mono-num font-bold`}>{num(x.shipments)}</td>
                <td className={`${TD} text-right font-mono-num`}>{inrCompact(x.gmv)}</td>
                <td className={`${TD} text-right font-mono-num`}>{inr(x.avg_ticket)}</td>
                <td className={`${TD} text-right font-mono-num ${x.rto_pct >= 20 ? "text-rose-600 dark:text-rose-400 font-bold" : x.rto_pct < 12 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>{pct(x.rto_pct)}</td>
                <td className={`${TD} text-right font-mono-num`}>{pct(x.cod_share_pct, 0)}</td>
                <td className={`${TD} text-right font-mono-num ${x.payment_conversion_pct < 80 ? "text-rose-600 dark:text-rose-400" : ""}`}>{pct(x.payment_conversion_pct)}</td>
                <td className={`${TD} text-right font-mono-num`}>{inrCompact(x.revenue)}</td>
                <td className={`${TD} text-right font-mono-num`}>{pct(x.margin_pct)}</td>
                <td className={TD}><ScoreBar value={x.score} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function CarrierBoard({ tick }) {
  const [days, setDays] = useState(90);
  const d = useData("/agg/analytics/carriers", { days }, tick);
  const sort = useSort(d?.items || [], "rank", "asc");
  const exp = () => downloadCsv(`carrier-sla-leaderboard-${days || "all"}d.csv`, sort.sorted, [
    ["rank", "Rank"], ["name", "Carrier"], ["mode", "Mode"], ["shipments", "Shipments"], ["avg_delivery_days", "Avg delivery (days)"], ["on_time_pct", "On-time %"],
    ["damaged_pct", "Damaged %"], ["rto_pct", "RTO %"], ["cost_per_kg", "Cost per kg (INR)"], ["cost_per_parcel", "Cost per parcel (INR)"], ["cost_efficiency_score", "Cost-efficiency score"]]);
  if (!d) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const chart = d.items.map((x) => ({ name: x.name, "Avg delivery (d)": x.avg_delivery_days, "Damaged %": x.damaged_pct, "Score / 10": +(x.cost_efficiency_score / 10).toFixed(1) }));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <div className="[&_select]:bg-background [&_select]:text-foreground [&_select]:border-input"><PeriodSelect value={days} onChange={setDays} testid="carrier-period" /></div>
        <CsvButton onClick={exp} testid="carrier-csv" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3" data-testid="carrier-cards">
        {d.items.map((x) => (
          <div key={x.carrier_id} className={`border bg-card p-3 space-y-2 ${x.rank === 1 ? "border-primary" : "border-border"}`} data-testid={`carrier-card-${x.rank}`}>
            <div className="flex items-start justify-between gap-2">
              <div><div className="font-heading font-black text-base leading-tight">{x.name}</div><div className="text-[10px] text-muted-foreground">{x.mode} · SLA {x.sla_days}d</div></div>
              <div className={`font-heading font-black text-xl ${x.rank === 1 ? "text-primary" : "text-muted-foreground"}`}>#{x.rank}</div>
            </div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Cost-efficiency score</div><ScoreBar value={x.cost_efficiency_score} /></div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              <span className="text-muted-foreground">Avg delivery</span><span className="text-right font-mono-num font-bold">{x.avg_delivery_days}d</span>
              <span className="text-muted-foreground">On-time</span><span className="text-right font-mono-num">{pct(x.on_time_pct)}</span>
              <span className="text-muted-foreground">Damaged</span><span className={`text-right font-mono-num ${x.damaged_pct >= 1 ? "text-rose-600 dark:text-rose-400 font-bold" : ""}`}>{x.damaged_pct}%</span>
              <span className="text-muted-foreground">RTO</span><span className="text-right font-mono-num">{pct(x.rto_pct)}</span>
              <span className="text-muted-foreground">Cost / kg</span><span className="text-right font-mono-num">{inr(x.cost_per_kg, 2)}</span>
              <span className="text-muted-foreground">Parcels</span><span className="text-right font-mono-num">{num(x.shipments)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
        <Panel className="xl:col-span-2" testid="carrier-chart" title="Side-by-side comparison" bodyClass="p-3">
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} interval={0} />
                <YAxis tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                <Tooltip contentStyle={TT} />
                <Bar dataKey="Avg delivery (d)" fill={CHART_COLORS[1]} />
                <Bar dataKey="Damaged %" fill={CHART_COLORS[5]} />
                <Bar dataKey="Score / 10" fill={CHART_COLORS[0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel className="xl:col-span-3" testid="carrier-table" title="SLA leaderboard"
          subtitle={`Score weights: cost/kg ${d.weights.cost_per_kg}% · on-time ${d.weights.on_time}% · damage ${d.weights.damage}% · RTO ${d.weights.rto}% · speed ${d.weights.speed}%`} bodyClass="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className={TH_ROW}><tr>
              <SortTh k="rank" label="#" sort={sort} /><SortTh k="name" label="Carrier" sort={sort} /><SortTh k="avg_delivery_days" label="Avg delivery" sort={sort} align="right" />
              <SortTh k="on_time_pct" label="On-time" sort={sort} align="right" /><SortTh k="damaged_pct" label="Damaged" sort={sort} align="right" />
              <SortTh k="rto_pct" label="RTO" sort={sort} align="right" /><SortTh k="cost_per_kg" label="₹/kg" sort={sort} align="right" />
              <SortTh k="cost_efficiency_score" label="Score" sort={sort} />
            </tr></thead>
            <tbody className="divide-y divide-border">
              {sort.sorted.map((x) => (
                <tr key={x.carrier_id} className="hover:bg-muted/40">
                  <td className={`${TD} font-heading font-black ${x.rank === 1 ? "text-primary" : ""}`}>{x.rank}</td>
                  <td className={`${TD} font-semibold`}>{x.name}</td>
                  <td className={`${TD} text-right font-mono-num`}>{x.avg_delivery_days}d</td>
                  <td className={`${TD} text-right font-mono-num`}>{pct(x.on_time_pct)}</td>
                  <td className={`${TD} text-right font-mono-num`}>{x.damaged_pct}% <span className="text-muted-foreground">({x.damaged})</span></td>
                  <td className={`${TD} text-right font-mono-num`}>{pct(x.rto_pct)}</td>
                  <td className={`${TD} text-right font-mono-num`}>{inr(x.cost_per_kg, 2)}</td>
                  <td className={TD}><ScoreBar value={x.cost_efficiency_score} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}

function Spark({ data }) {
  const max = Math.max(1, ...data.map((x) => x.count));
  return (
    <div className="flex items-end gap-[2px] h-6 w-[70px]" title={data.map((x) => `${x.date.slice(5)}: ${x.count}`).join("  ")}>
      {data.map((x) => <div key={x.date} className="flex-1 bg-sky-500/70" style={{ height: `${Math.max(6, (x.count / max) * 100)}%` }} />)}
    </div>
  );
}

function HubUtil({ tick }) {
  const [q, setQ] = useState("");
  const d = useData("/agg/analytics/hubs", {}, tick);
  const rows = useMemo(() => (d?.items || []).filter((x) => textMatch(x, q, ["name", "code", "city", "franchisee_name", "region"])), [d, q]);
  const sort = useSort(rows, "utilisation_pct");
  const exp = () => downloadCsv("franchise-hub-utilisation.csv", sort.sorted, [
    ["name", "Hub"], ["code", "Code"], ["region", "Region"], ["franchisee_name", "Franchisee"], ["daily_capacity", "Daily capacity"], ["today_intake", "Intake today"],
    ["pending_scan_ins", "Pending scan-ins"], ["backlog", "Backlog (older)"], ["utilisation_pct", "Utilisation %"], ["headroom", "Headroom"],
    ["local_distribution_efficiency_pct", "Local distribution efficiency %"], ["local_avg_tat_days", "Local avg TAT (days)"], ["avg_7d", "7-day avg intake"]]);
  if (!d) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const s = d.summary;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi testid="hu-kpi-cap" label="Network daily capacity" icon={Gauge} value={num(s.capacity)} sub="parcels / day across hubs" />
        <Kpi testid="hu-kpi-load" label="Load today" icon={Package} accent="text-sky-500" value={num(s.load)} sub={`${pct(s.capacity ? (s.load / s.capacity) * 100 : 0)} of capacity`} />
        <Kpi testid="hu-kpi-pending" label="Pending scan-ins" icon={Inbox} accent="text-amber-500" value={num(s.pending)} sub="manifested, awaiting carrier pick-up" />
        <Kpi testid="hu-kpi-near" label={`Hubs ≥ ${d.threshold_pct}% capacity`} icon={AlertTriangle} accent="text-rose-500" value={num(s.near_capacity)} sub="divert pickups or add a shift" />
      </div>
      <Panel testid="hub-util-table" title="Franchise hub utilisation" subtitle={`Load = today's intake + older parcels still waiting. Rows turn red at ${d.threshold_pct}% of daily capacity. Local efficiency = same-region parcels delivered within SLA.`}
        actions={<><TableFilter testid="hu-search" value={q} onChange={setQ} placeholder="Filter hub, city…" /><CsvButton onClick={exp} testid="hu-csv" /></>} bodyClass="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className={TH_ROW}><tr>
            <SortTh k="name" label="Hub" sort={sort} /><SortTh k="daily_capacity" label="Capacity/day" sort={sort} align="right" />
            <SortTh k="today_intake" label="Intake today" sort={sort} align="right" /><SortTh k="pending_scan_ins" label="Pending scan-ins" sort={sort} align="right" />
            <SortTh k="utilisation_pct" label="Utilisation" sort={sort} /><SortTh k="headroom" label="Headroom" sort={sort} align="right" />
            <SortTh k="oldest_pending_hours" label="Oldest wait" sort={sort} align="right" /><SortTh k="local_distribution_efficiency_pct" label="Local efficiency" sort={sort} align="right" />
            <SortTh k="local_avg_tat_days" label="Local TAT" sort={sort} align="right" /><th className="px-3 py-2 text-left">7-day intake</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {sort.sorted.map((x) => (
              <tr key={x.hub_id} className={x.near_capacity ? "bg-rose-500/10 hover:bg-rose-500/15" : "hover:bg-muted/40"} data-testid={`hu-row-${x.code}`}>
                <td className={TD}>
                  <div className={`font-semibold flex items-center gap-1.5 ${x.near_capacity ? "text-rose-600 dark:text-rose-400" : ""}`}>{x.near_capacity && <AlertTriangle className="h-3.5 w-3.5" />}{x.name}</div>
                  <div className="text-[10px] text-muted-foreground">{x.franchisee_name} · {x.region}</div>
                </td>
                <td className={`${TD} text-right font-mono-num`}>{x.daily_capacity}</td>
                <td className={`${TD} text-right font-mono-num font-bold`}>{x.today_intake}</td>
                <td className={`${TD} text-right font-mono-num`}>{x.pending_scan_ins}{x.backlog > 0 && <span className="text-[10px] text-amber-600 dark:text-amber-400"> ({x.backlog} old)</span>}</td>
                <td className={TD}>
                  <div className="flex items-center gap-2 min-w-[130px]">
                    <div className="flex-1 h-2 bg-muted"><div className={`h-full ${x.near_capacity ? "bg-rose-500" : x.utilisation_pct >= 65 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, x.utilisation_pct)}%` }} /></div>
                    <span className={`font-mono-num w-12 text-right font-bold ${x.near_capacity ? "text-rose-600 dark:text-rose-400" : ""}`}>{pct(x.utilisation_pct, 0)}</span>
                  </div>
                  {x.over_capacity && <Pill className="bg-rose-600 text-white border-rose-600 mt-1">Over capacity</Pill>}
                </td>
                <td className={`${TD} text-right font-mono-num`}>{x.headroom}</td>
                <td className={`${TD} text-right font-mono-num`}>{x.pending_scan_ins ? `${x.oldest_pending_hours}h` : "—"}</td>
                <td className={`${TD} text-right font-mono-num ${x.local_distribution_efficiency_pct < 65 ? "text-rose-600 dark:text-rose-400" : ""}`}>{pct(x.local_distribution_efficiency_pct)}<div className="text-[10px] text-muted-foreground">{x.local_parcels} local</div></td>
                <td className={`${TD} text-right font-mono-num`}>{x.local_avg_tat_days}d</td>
                <td className={TD}><Spark data={x.trend_7d} /><div className="text-[10px] text-muted-foreground">avg {x.avg_7d}/day</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export default function AggAnalyticsPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "msme";
  const [tick, setTick] = useState(0);
  return (
    <div className="space-y-4" data-testid="agg-analytics-page">
      <AggHeader icon={BarChart3} title="Stakeholder Analytics & Directory"
        subtitle="Rankings for MSMEs, 3PL carriers and franchise hubs. Every table exports to CSV."
        actions={<ResetSampleButton onDone={() => setTick((t) => t + 1)} />} />
      <TabBar tabs={TABS} value={tab} onChange={(k) => setParams({ tab: k }, { replace: true })} testid="an-tabs" />
      {tab === "msme" && <MsmeMatrix tick={tick} />}
      {tab === "carriers" && <CarrierBoard tick={tick} />}
      {tab === "hubs" && <HubUtil tick={tick} />}
    </div>
  );
}
